// Sharebox's D5 "confirm profile" sub-flow has two kinds of steps:
// - a fixed prefix, once per reservation (identity+address, privacy, "are you the driver?")
// - a per-driver cycle (documents, driver details, "add another driver?") that repeats
//   once per driver actually added — 1, 2, or 3 times, not known in advance.
//
const MAX_DRIVERS = 3;

const PREFIX_STEPS = [
  "BillingDetails",
  "PrivacyPolicyForm",
  "RentalContractFirstDriver",
] as const;
const PER_DRIVER_STEPS = [
  "UploadDocuments",
  "DriverDetails",
  "AdditionalDriver",
] as const;

const STEP_WEIGHTS: Record<string, number> = {
  BillingDetails: 4,
  PrivacyPolicyForm: 1,
  RentalContractFirstDriver: 1,
  UploadDocuments: 1,
  DriverDetails: 6,
  AdditionalDriver: 1,
};

const PREFIX_WEIGHT = PREFIX_STEPS.reduce(
  (sum, step) => sum + STEP_WEIGHTS[step],
  0
);
const PER_DRIVER_WEIGHT = PER_DRIVER_STEPS.reduce(
  (sum, step) => sum + STEP_WEIGHTS[step],
  0
);

export interface ShareboxProgressParams {
  /** The current top-level step name (e.g. "BillingDetails", "UploadDocuments"). */
  page: string;
  /** 1-based index of the step's own internal section, if it has sub-sections. */
  currentSection: number;
  /** Total number of internal sections for this step (1 if it has none). */
  totalSections: number;
  /** 0-based index of the driver cycle currently in progress (0 for the first driver). */
  currentDriver: number;
}

export const calculateShareboxProgress = ({
  page,
  currentSection,
  totalSections,
  currentDriver,
}: ShareboxProgressParams): number => {
  const totalWeight = PREFIX_WEIGHT + PER_DRIVER_WEIGHT * MAX_DRIVERS;

  const sectionFraction =
    totalSections > 0
      ? Math.min(Math.max(currentSection, 0) / totalSections, 1)
      : 1;

  let accumulatedWeight = 0;

  const prefixIndex = PREFIX_STEPS.indexOf(page as typeof PREFIX_STEPS[number]);
  if (prefixIndex !== -1) {
    for (let i = 0; i < prefixIndex; i++) {
      accumulatedWeight += STEP_WEIGHTS[PREFIX_STEPS[i]];
    }
    accumulatedWeight += STEP_WEIGHTS[page] * sectionFraction;
  } else {
    accumulatedWeight += PREFIX_WEIGHT;
    accumulatedWeight += PER_DRIVER_WEIGHT * Math.max(currentDriver, 0);

    const perDriverIndex = PER_DRIVER_STEPS.indexOf(
      page as typeof PER_DRIVER_STEPS[number]
    );
    if (perDriverIndex !== -1) {
      for (let i = 0; i < perDriverIndex; i++) {
        accumulatedWeight += STEP_WEIGHTS[PER_DRIVER_STEPS[i]];
      }
      accumulatedWeight += STEP_WEIGHTS[page] * sectionFraction;
    }
  }

  return Math.min((accumulatedWeight / totalWeight) * 100, 98);
};
